import React, { useContext, useState } from 'react';
import { LearnerActivity } from '../../../../../models/LearnerCourse';
import useModifyRequest from '../../../../../hooks/useModifyRequest';
import LearnerQuestionnaireContent from './LearnerQuestionnaireContent';
import { QuestionnaireAnswer } from '../../../../../models/QuestionnaireAnswer';
import { ApiResponse } from '../../../../../lib/_api-helpers';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import CompleteCourseModal from 'components/modals/CompleteCourseModal';

type LearnerQuestionnaireProps = {
    questionnaire: LearnerActivity;
};

type AnswerFormData = {
    answer: string;
};

type SetActivityCompleteRequest = {
    completed: Date | null;
};

export default function LearnerQuestionnaire({ questionnaire }: LearnerQuestionnaireProps): JSX.Element {
    const learnerCourse = useContext(LearnerCourseContext).learnerCourse!;
    const { previewMode, setCourseState, isFinalActivity } = useContext(LearnerCourseContext);
    const [currentQuestionNumber, setCurrentQuestionNumber] = React.useState<number | null>(null);
    const [errors, setErrors] = React.useState<boolean>(false);
    const [showCompleteCourseModal, setShowCompleteCourseModal] = useState<boolean>(false);

    const { modifyData: setActivityCompletion } = useModifyRequest<SetActivityCompleteRequest, boolean>(
        `learner/courses/${learnerCourse.enrollmentId}/activity/${questionnaire.activityStatusId}/complete`,
        'POST',
    );

    const currentQuestion =
        questionnaire.questions.find((x) => x.order === currentQuestionNumber) ??
        (currentQuestionNumber !== null ? questionnaire.questions[currentQuestionNumber] : undefined);

    const { modifyData: postAnswer } = useModifyRequest<AnswerFormData, QuestionnaireAnswer>(
        `learner/courses/${learnerCourse.enrollmentId}/questionnaire/${questionnaire.activityStatusId}/question/${currentQuestion?.id ?? null}`,
        'POST',
    );

    const submitAnswer = async (answer: string): Promise<ApiResponse<QuestionnaireAnswer>> => {
        return await postAnswer({ answer: answer });
    };

    async function completeQuestionnaire(): Promise<ApiResponse<boolean>> {
        const now = new Date();

        if (!previewMode) {
            const response = await setActivityCompletion({ completed: now });

            if (response.errors?.length) {
                setErrors(true);
                return { value: false };
            }
        }

        setCourseState((prev) => {
            if (!prev) return prev;

            const moduleIndex = prev.modules.findIndex((module) => module.id === questionnaire.moduleId);

            const activityIndex = prev.modules[moduleIndex].activities.findIndex(
                (activity) => activity.activityStatusId === questionnaire.activityStatusId,
            );

            const newModules = [...prev.modules];
            const newActivities = [...newModules[moduleIndex].activities];

            newActivities[activityIndex] = {
                ...newActivities[activityIndex],
            };

            newActivities[activityIndex].completed = true;

            newModules[moduleIndex] = {
                ...newModules[moduleIndex],
                activities: newActivities,
            };

            return {
                ...prev,
                modules: newModules,
            };
        });

        if (isFinalActivity) {
            setShowCompleteCourseModal(true);
        }

        return { value: true };
    }

    return (
        <>
            {showCompleteCourseModal && <CompleteCourseModal />}
            <LearnerQuestionnaireContent
                questionnaire={questionnaire}
                currentQuestionNumber={currentQuestionNumber}
                currentQuestion={currentQuestion}
                setActivityToComplete={completeQuestionnaire}
                answerQuestion={submitAnswer}
                setQuestionNumber={setCurrentQuestionNumber}
                completionErrors={errors}
            />
        </>
    );
}
